/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useState } from 'react';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { api, type AccessSurface, type AccessTool, type PendingAccessChange } from '../lib/api';
import { errorText } from '../lib/errors';
import { Button, Callout, EmptyState, ErrorState, Input, PageHeader, Panel, RISK_CHIPS, SpecChip, Stack, StatusChip, Table, Tag, type Column } from '../ui';
import { count, plural } from '../ui/format';

/** A tool with the app it belongs to: the one table lists every app's tools, grouped by app. */
type ToolRow = AccessTool & { app: string };

/**
 * People › Access & roles, rebuilt on the kit (overhaul plan step 3e): IT staff set which roles may use each tool or
 * connector. Restricting applies immediately; broadening (a grant) is routed to a SECOND IT-admin to approve
 * (separation of duties). Role membership itself is managed in Entra; this screen controls the per-tool gates. The same
 * calls as before: one table of every tool grouped by app in place of a card per app, the second-approver queue as a
 * panel above it, and the result as a callout in the service's words.
 */
export function Access() {
  const [surfaces, setSurfaces] = useState<AccessSurface[]>([]);
  const [pending, setPending] = useState<PendingAccessChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loadError, setLoadError] = useState<unknown>(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.access();
      setSurfaces(r.surfaces);
      setPending(r.pending);
    } catch (e) {
      // A failed read is said on the page, with Try again; nothing on it was a stored value.
      setLoadError(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const save = async (app: string, tool: string, rolesCsv: string) => {
    const roles = rolesCsv.split(',').map((s) => s.trim()).filter(Boolean);
    const k = `${app}.${tool}`;
    setBusy(k);
    setMsg('');
    try {
      const r = await api.setAccess(app, tool, roles);
      setMsg(r.status === 'pending_approval' ? `“${k}” broadens access — sent to a second admin for approval.` : `“${k}” updated.`);
      setDraft((d) => {
        const n = { ...d };
        delete n[k];
        return n;
      });
      await load();
    } catch (e) {
      // The service names the refused field (for example roles.0 for an empty role); nothing was stored.
      setMsg(`“${k}” was not saved: ${errorText(e, 'the service refused the change.')}`);
    } finally {
      setBusy(null);
    }
  };

  const decide = async (id: string, decision: 'approve' | 'reject') => {
    setBusy(id);
    setMsg('');
    try {
      await api.decideAccess(id, decision);
      await load();
    } catch (e) {
      setMsg(`The ${decision === 'approve' ? 'approval' : 'rejection'} was not recorded: ${errorText(e, 'the service refused it.')}`);
    } finally {
      setBusy(null);
    }
  };

  const tools: ToolRow[] = surfaces.flatMap((s) => s.tools.map((t) => ({ ...t, app: s.key })));
  const refused = /was not (saved|recorded)/.test(msg);

  const pendingColumns: Array<Column<PendingAccessChange>> = [
    { key: 'tool', header: 'Tool', cell: (c) => <Tag>{c.app}.{c.tool}</Tag> },
    { key: 'from', header: 'From', muted: true, cell: (c) => c.oldRoles.join(', ') || 'everyone' },
    { key: 'to', header: 'To', muted: true, cell: (c) => c.newRoles.join(', ') || 'everyone' },
    { key: 'by', header: 'Requested by', muted: true, cell: (c) => c.requestedBy },
    {
      key: 'actions', header: <span className="ui-sr">Actions</span>, align: 'end',
      cell: (c) => (
        <span className="ui-row-actions">
          <Button variant="primary" size="sm" busy={busy === c.id} onClick={() => void decide(c.id, 'approve')}>Approve</Button>
          <Button size="sm" disabled={busy === c.id} onClick={() => void decide(c.id, 'reject')}>Reject</Button>
        </span>
      ),
    },
  ];

  const toolColumns: Array<Column<ToolRow>> = [
    { key: 'tool', header: 'Tool', cell: (t) => <Tag>{t.name}</Tag> },
    { key: 'mode', header: 'Mode', muted: true, cell: (t) => t.mode },
    { key: 'risk', header: 'Risk', cell: (t) => (t.riskTier in RISK_CHIPS ? <SpecChip spec={RISK_CHIPS[t.riskTier as keyof typeof RISK_CHIPS]} /> : <StatusChip tone="neutral">{t.riskTier}</StatusChip>) },
    {
      key: 'effective', header: 'Effective roles', muted: true,
      cell: (t) => (
        <span className="ui-cell-title">
          <span>{t.effectiveRoles.join(', ') || 'everyone'}</span>
          {t.override !== null && <StatusChip tone="accent" plain>override</StatusChip>}
        </span>
      ),
    },
    {
      key: 'also', header: 'Also requires', muted: true, hideOnPhone: true,
      cell: (t) => <span title={t.andTighten.length ? 'Set on the Apps screen — a caller must ALSO hold one of these, on top of the effective roles.' : ''}>{t.andTighten.length ? t.andTighten.join(', ') : '—'}</span>,
    },
    {
      key: 'set', header: 'Set roles', width: '26%',
      cell: (t) => {
        const k = `${t.app}.${t.name}`;
        const current = (t.override ?? t.effectiveRoles).join(', ');
        const val = draft[k] ?? current;
        return (
          <span className="ui-actions">
            <Input size="sm" className="ui-grow" aria-label={`Roles for ${k}`} value={val} changed={val !== current} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} placeholder="admin, procore_user" />
            <Button variant="primary" size="sm" busy={busy === k} disabled={val === current} onClick={() => void save(t.app, t.name, val)}>Save</Button>
          </span>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        meta={loading ? <span>Loading…</span> : <span>{plural(tools.length, 'tool')} across {plural(surfaces.length, 'app')}</span>}
        actions={pending.length > 0 ? <StatusChip tone="warn" icon={ShieldCheck}>{count(pending.length)} awaiting a second approver</StatusChip> : null}
      />
      <div className="ui-page">
        <Stack>
          {msg && <Callout tone={refused ? 'bad' : 'ok'} role={refused ? 'alert' : 'status'}>{msg}</Callout>}
          {loadError ? <ErrorState title="Access & roles could not be loaded" error={loadError} onRetry={() => void load()} retrying={loading} /> : null}

          {pending.length > 0 && (
            <Panel
              flush
              title={<span className="ui-title-icon"><ShieldCheck size={16} strokeWidth={1.75} aria-hidden="true" />Awaiting a second approver</span>}
              description="Changes that broaden access wait for a different admin"
              footer={<span>You can’t approve your own request — a different admin must.</span>}
            >
              <Table stacked label="Awaiting a second approver" columns={pendingColumns} rows={pending} rowKey={(c) => c.id} stickyHeader={false} />
            </Panel>
          )}

          {!loadError && (
          <Panel
            flush
            title={<span className="ui-title-icon"><KeyRound size={16} strokeWidth={1.75} aria-hidden="true" />Tools</span>}
            description="Which roles may use each tool; blank means everyone"
            footer={<span>Restricting access applies immediately; broadening it is sent to a second IT-admin to approve (separation of duties). Role membership itself is managed in Entra. Roles are comma-separated.</span>}
          >
            <Table stacked
              label="Tools"
              columns={toolColumns}
              rows={tools}
              rowKey={(t) => `${t.app}.${t.name}`}
              groupOf={(t) => t.app}
              loading={loading}
              skeletonRows={6}
              className="ui-table--inputs"
              empty={<EmptyState compact icon={KeyRound} title="No tools registered">Each app registers its tools with its surface.</EmptyState>}
            />
          </Panel>
          )}
        </Stack>
      </div>
    </>
  );
}
